import type {
  ConsentManager,
  ConsentSnapshot,
  DestinationContext,
  DestinationFilter,
  DestinationKey,
  IdentityState,
  TrackEvent,
  TrackOptions,
  TrackingDestination,
} from './types'
import { buildReplayEventId, shouldReplayOnConsent } from './consentReplay'
import { eventRegistry } from './registry'

export interface TrackingClientConfig {
  consentManager: ConsentManager
  destinations: TrackingDestination[]
  /**
   * Default destinations when an event isn't in the registry and no call override is provided.
   * Recommended: ['kissmetrics'] to keep KM as primary and avoid surprising emissions.
   */
  defaultDestinations: DestinationKey[]
}

type PendingConsentTrack = {
  destinationKey: DestinationKey
  eventName: string
  properties?: Record<string, any>
  eventId: string
}

type PendingConsentIdentify = {
  destinationKey: DestinationKey
  userId: string
  traits?: Record<string, any>
}

function consentAllows(
  required: TrackingDestination['requiredConsent'],
  consent: ConsentSnapshot
) {
  if (required === null) return true
  if (required === 'functional') return consent.functional
  if (required === 'statistics') return consent.statistics
  if (required === 'marketing') return consent.marketing
  // 'necessary' isn't represented in our snapshot; treat as always allowed.
  return true
}

function normalizeDestinationFilter(
  filter?: DestinationFilter
): { include?: Set<DestinationKey>; exclude?: Set<DestinationKey> } {
  if (!filter) return {}
  if (Array.isArray(filter)) return { include: new Set(filter) }
  return {
    include: filter.include ? new Set(filter.include) : undefined,
    exclude: filter.exclude ? new Set(filter.exclude) : undefined,
  }
}

export class TrackingClient {
  private readonly consentManager: ConsentManager
  private readonly destinations: Map<DestinationKey, TrackingDestination>
  private identity: IdentityState = {}

  private consent: ConsentSnapshot
  /** Set by `page()` so destinations (e.g. Zoho PageSense) can scope script init to checkout after SPA navigations. */
  private currentPath: string | null = null
  private currentPageProperties?: Record<string, any>
  private currentPageOptions?: TrackOptions
  private pendingConsentTracks: PendingConsentTrack[] = []
  private pendingConsentIdentifies: PendingConsentIdentify[] = []
  private readonly defaultDestinations: DestinationKey[]
  private readonly debug: boolean

  constructor(config: TrackingClientConfig) {
    this.consentManager = config.consentManager
    this.defaultDestinations = config.defaultDestinations
    this.destinations = new Map(config.destinations.map(d => [d.key, d]))
    this.debug = process.env.NEXT_PUBLIC_TRACKING_DEBUG === '1'

    this.consent = this.consentManager.getSnapshot()
    this.consentManager.onChange(next => {
      const previousConsent = this.consent
      this.consent = next
      this.initEligibleDestinations()
      this.replayCurrentPageForNewConsent(previousConsent)
      this.replayPendingTracksForNewConsent()
      this.replayPendingIdentifiesForNewConsent()
    })

    this.initEligibleDestinations()
  }

  private getDestinationContext(): DestinationContext {
    return {
      identity: this.identity,
      consent: this.consent,
      currentPath: this.currentPath,
    }
  }

  private initEligibleDestinations() {
    const ctx = this.getDestinationContext()
    this.destinations.forEach(dest => {
      // Always allow init if destination wants to run pre-consent; otherwise gate by required consent.
      if (
        dest.init &&
        (dest.allowCallsBeforeConsent === true ||
          consentAllows(dest.requiredConsent, this.consent))
      ) {
        dest.init(ctx)
      }
    })
  }

  private replayCurrentPageForNewConsent(previousConsent: ConsentSnapshot) {
    if (!this.currentPath) return

    const ctx = this.getDestinationContext()
    const destinations = this.resolveDestinations('page', this.currentPageOptions)

    destinations.forEach(key => {
      const dest = this.destinations.get(key)
      if (!dest?.page) return

      const wasAllowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, previousConsent)
      const isAllowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      if (wasAllowed || !isAllowed) return
      dest.page(this.currentPath!, this.currentPageProperties, ctx)
    })
  }

  private replayPendingTracksForNewConsent() {
    if (this.pendingConsentTracks.length === 0) return

    const ctx = this.getDestinationContext()
    const remaining: PendingConsentTrack[] = []

    this.pendingConsentTracks.forEach(pending => {
      const dest = this.destinations.get(pending.destinationKey)
      if (!dest?.track) return

      const isAllowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      if (!isAllowed) {
        remaining.push(pending)
        return
      }

      dest.track(pending.eventName, pending.properties, ctx, pending.eventId)
    })

    this.pendingConsentTracks = remaining
  }

  private queuePendingConsentTrack(pending: PendingConsentTrack) {
    if (
      this.pendingConsentTracks.some(
        item =>
          item.destinationKey === pending.destinationKey &&
          item.eventId === pending.eventId
      )
    ) {
      return
    }

    this.pendingConsentTracks.push(pending)
  }

  private replayPendingIdentifiesForNewConsent() {
    if (this.pendingConsentIdentifies.length === 0) return

    const ctx = this.getDestinationContext()
    const remaining: PendingConsentIdentify[] = []

    this.pendingConsentIdentifies.forEach(pending => {
      const dest = this.destinations.get(pending.destinationKey)
      if (!dest?.identify) return

      const isAllowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      if (!isAllowed) {
        remaining.push(pending)
        return
      }

      dest.identify(pending.userId, pending.traits, ctx)
    })

    this.pendingConsentIdentifies = remaining
  }

  private queuePendingConsentIdentify(pending: PendingConsentIdentify) {
    this.pendingConsentIdentifies = this.pendingConsentIdentifies.filter(
      item => item.destinationKey !== pending.destinationKey
    )
    this.pendingConsentIdentifies.push(pending)
  }

  private resolveDestinations(
    eventName: string,
    options?: TrackOptions
  ): DestinationKey[] {
    const def = eventRegistry[eventName]
    const base = def?.defaultDestinations ?? this.defaultDestinations

    const filter = normalizeDestinationFilter(options?.destinations)
    let result = base

    if (filter.include) {
      const filtered = result.filter(d => filter.include!.has(d))
      // If the registry default set does not overlap the explicit include list
      // (e.g. Kissmetrics-only default + destinations: ['zohoPageSense']), honor the include list.
      result = filtered.length > 0 ? filtered : Array.from(filter.include)
    }
    if (filter.exclude) {
      result = result.filter(d => !filter.exclude!.has(d))
    }

    // Ensure uniqueness while preserving order
    const seen = new Set<DestinationKey>()
    return result.filter(d => (seen.has(d) ? false : (seen.add(d), true)))
  }

  identify(userId: string, traits?: Record<string, any>, options?: TrackOptions) {
    this.identity = {
      ...this.identity,
      email: userId,
      customerId: traits?.customerId ?? this.identity.customerId,
      traits: { ...(this.identity.traits ?? {}), ...(traits ?? {}) },
    }

    const ctx = this.getDestinationContext()
    const destinations = this.resolveDestinations('identify', options)
    if (this.debug) {
      console.debug('[tracking.identify]', { userId, destinations, traits })
    }

    destinations.forEach(key => {
      const dest = this.destinations.get(key)
      if (!dest?.identify) return

      const allowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      if (!allowed) {
        if (shouldReplayOnConsent(dest, options)) {
          this.queuePendingConsentIdentify({
            destinationKey: key,
            userId,
            traits,
          })
        }
        return
      }
      dest.identify(userId, traits, ctx)
    })
  }

  track(eventName: string, properties?: Record<string, any>, options?: TrackOptions) {
    const ctx = this.getDestinationContext()
    const event: TrackEvent = {
      name: eventName,
      properties,
      eventId: options?.eventId,
      timestamp: new Date(),
    }

    const def = eventRegistry[eventName]
    const destinations = this.resolveDestinations(eventName, options)
    if (this.debug) {
      console.debug('[tracking.track]', { eventName, destinations, properties })
    }

    destinations.forEach(key => {
      const dest = this.destinations.get(key)
      if (!dest?.track) return

      const allowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      const override =
        def?.destinations?.[key]?.map?.(event) ?? {
          eventName: eventName,
          properties,
        }
      if (!override) return

      if (!allowed) {
        if (shouldReplayOnConsent(dest, options)) {
          this.queuePendingConsentTrack({
            destinationKey: key,
            eventName: override.eventName,
            properties: override.properties,
            eventId: buildReplayEventId(
              eventName,
              override.eventName,
              override.properties,
              event.eventId
            ),
          })
        }
        return
      }

      dest.track(override.eventName, override.properties, ctx, event.eventId)
    })
  }

  page(path: string, properties?: Record<string, any>, options?: TrackOptions) {
    this.currentPath = path
    this.currentPageProperties = properties
    this.currentPageOptions = options
    const ctx = this.getDestinationContext()
    const destinations = this.resolveDestinations('page', options)
    if (this.debug) {
      console.debug('[tracking.page]', { path, destinations, properties })
    }

    destinations.forEach(key => {
      const dest = this.destinations.get(key)
      if (!dest?.page) return

      const allowed =
        dest.allowCallsBeforeConsent === true ||
        consentAllows(dest.requiredConsent, this.consent)

      if (!allowed) return
      dest.page(path, properties, ctx)
    })

    this.initEligibleDestinations()
  }

  reset() {
    this.identity = {}
    const ctx = this.getDestinationContext()
    this.destinations.forEach(dest => dest.reset?.(ctx))
  }
}
